import type { CoachAthleteEdge } from "./schemas/coachAthleteEdge.js";

// Bucket a caller's CoachAthleteEdge rows by relationship + status, from the
// CALLER's perspective. Extracted from mobile's CoachEdgesContext so mobile and
// the web-rnw coach settings compute the same six groups from one definition
// (the grouping is the reusable core; the fetch/AppState/mutation wiring stays
// per-platform). `revoked` edges are dropped (treated as absent by every
// permission check). Pure.

export type CoachEdgeGroups = {
  athletesActive: CoachAthleteEdge[];
  athletesPendingOut: CoachAthleteEdge[];
  athletesPendingIn: CoachAthleteEdge[];
  coachesActive: CoachAthleteEdge[];
  coachesPendingOut: CoachAthleteEdge[];
  coachesPendingIn: CoachAthleteEdge[];
};

export const emptyCoachEdgeGroups = (): CoachEdgeGroups => ({
  athletesActive: [],
  athletesPendingOut: [],
  athletesPendingIn: [],
  coachesActive: [],
  coachesPendingOut: [],
  coachesPendingIn: [],
});

export const groupCoachEdges = (
  edges: ReadonlyArray<CoachAthleteEdge>,
  callerSub: string | null,
): CoachEdgeGroups => {
  const out = emptyCoachEdgeGroups();
  if (!callerSub) return out;
  for (const e of edges) {
    if (e.status === "revoked") continue;
    const callerIsCoach = e.coachSub === callerSub;
    if (callerIsCoach) {
      if (e.status === "active") out.athletesActive.push(e);
      else if (e.status === "invited_by_coach") out.athletesPendingOut.push(e);
      else if (e.status === "invited_by_athlete") out.athletesPendingIn.push(e);
    } else {
      if (e.status === "active") out.coachesActive.push(e);
      else if (e.status === "invited_by_athlete") out.coachesPendingOut.push(e);
      else if (e.status === "invited_by_coach") out.coachesPendingIn.push(e);
    }
  }
  return out;
};

// Total edges awaiting THIS caller's decision (drives banner/badge counts).
export const pendingInCount = (g: CoachEdgeGroups): number =>
  g.athletesPendingIn.length + g.coachesPendingIn.length;

// Any edge in any group (drives the "no relationships yet" empty state).
export const hasAnyEdges = (g: CoachEdgeGroups): boolean =>
  g.athletesActive.length +
    g.athletesPendingIn.length +
    g.athletesPendingOut.length +
    g.coachesActive.length +
    g.coachesPendingIn.length +
    g.coachesPendingOut.length >
  0;
